import { ApolloClient, gql, NormalizedCacheObject, useMutation, useQuery, useLazyQuery } from "@apollo/client";
import { Query, Mutation, EdgeQueueTaskType, TaskState, BullmqJobState } from "../types/generated/graphql";

export const useGetFailedTasks = (
  client: ApolloClient<NormalizedCacheObject>,
  limit: number = 20,
  taskType?: EdgeQueueTaskType
) => {
  type T = Pick<Query, 'getFailedTasks'>;
  const ret = useQuery<T>(QUERY_GET_FAILED_TASKS, {
    client,
    variables: { limit, taskType },
    fetchPolicy: "network-only",
  });

  return ret;
};

/**
 * Light-weight profile-name lookup for admin views. Returns just
 * `{id, name}` so the Edge Tasks table can render an initials
 * avatar per row without paying the cost of the full
 * YouTubeProfile shape (which carries thumbnails, status sidecar
 * fields, preferences, etc.). The fields it does select are all
 * stored inline on the profile doc, so the field-resolvers that
 * lazily load runtime status don't fire.
 */
export const useGetAllYouTubeProfilesLite = (client: ApolloClient<NormalizedCacheObject>) => {
  type T = {getAllYouTubeProfiles: Array<{id: string; name: string} | null> | null};
  return useQuery<T>(QUERY_GET_ALL_PROFILES_LITE, {
    client,
    fetchPolicy: 'cache-and-network',
  });
};

const QUERY_GET_ALL_PROFILES_LITE = gql`
  query getAllYouTubeProfilesLite {
    getAllYouTubeProfiles {
      id
      name
    }
  }
`;

/** Generic admin task query with multi-select filters for state + type. */
export const useGetAdminTasks = (
  client: ApolloClient<NormalizedCacheObject>,
  params: { states?: TaskState[]; taskTypes?: EdgeQueueTaskType[]; limit?: number }
) => {
  type T = Pick<Query, 'getAdminTasks'>;
  return useQuery<T>(QUERY_GET_ADMIN_TASKS, {
    client,
    variables: {
      states: params.states && params.states.length > 0 ? params.states : null,
      taskTypes: params.taskTypes && params.taskTypes.length > 0 ? params.taskTypes : null,
      limit: params.limit ?? 100,
    },
    fetchPolicy: "cache-and-network",
  });
};

const QUERY_GET_ADMIN_TASKS = gql`
  query getAdminTasks($states: [TaskState!], $taskTypes: [EdgeQueueTaskType!], $limit: Int) {
    getAdminTasks(states: $states, taskTypes: $taskTypes, limit: $limit) {
      id
      profileId
      type
      state
      # 'desktop' / 'mobile' / 'unknown'. Server populates this on
      # every ack branch (Failed / Completed-with-data / Completed-
      # no-data). Without selecting it here the admin Device column
      # always rendered the em-dash placeholder, even on freshly-
      # acked tasks — the field never made it into the response.
      executedBy
      # FK to the devices collection — written atomically with
      # state-FETCHING so the admin row knows which specific
      # device is currently holding the task (vs executedBy which
      # is a class tag set on ack). Null until first claim.
      claimedByDeviceId
      createdAt
      updatedAt
      startedAt
      completedAt
      retryCount
      maxRetries
      result {
        status
        errorMessage
      }
      failureStage
      failureReason
      failureMessage
      failedAt
      parserVersion
      extractionJobId
    }
  }
`;

/** Re-run extraction for a single edge task on the BullMQ worker. */
export const useRetryEdgeTaskExtraction = (
  client: ApolloClient<NormalizedCacheObject>,
) => {
  type T = Pick<Mutation, 'retryEdgeTaskExtraction'>;
  return useMutation<T, {taskId: string; dryRun?: boolean}>(
    MUTATION_RETRY_EDGE_TASK_EXTRACTION,
    {
      client,
      refetchQueries: ['getAdminTasks'],
    },
  );
};

const MUTATION_RETRY_EDGE_TASK_EXTRACTION = gql`
  mutation retryEdgeTaskExtraction($taskId: ID!, $dryRun: Boolean) {
    retryEdgeTaskExtraction(taskId: $taskId, dryRun: $dryRun) {
      ok
      jobId
      messageType
    }
  }
`;

/** Permanently delete a single edge task (Firestore doc + blob). */
export const useDeleteEdgeTask = (
  client: ApolloClient<NormalizedCacheObject>,
) => {
  type T = Pick<Mutation, 'deleteEdgeTask'>;
  return useMutation<T, {taskId: string}>(MUTATION_DELETE_EDGE_TASK, {
    client,
    refetchQueries: ['getAdminTasks'],
  });
};

const MUTATION_DELETE_EDGE_TASK = gql`
  mutation deleteEdgeTask($taskId: ID!) {
    deleteEdgeTask(taskId: $taskId) {
      deleted
    }
  }
`;

/** Bulk-delete edge tasks by explicit id list. Used by the
 *  admin tasks table's multi-select checkbox flow. */
export const useDeleteEdgeTasksByIds = (
  client: ApolloClient<NormalizedCacheObject>,
) => {
  type T = Pick<Mutation, 'deleteEdgeTasksByIds'>;
  return useMutation<T, {taskIds: string[]}>(MUTATION_DELETE_EDGE_TASKS_BY_IDS, {
    client,
    refetchQueries: ['getAdminTasks'],
  });
};

const MUTATION_DELETE_EDGE_TASKS_BY_IDS = gql`
  mutation deleteEdgeTasksByIds($taskIds: [ID!]!) {
    deleteEdgeTasksByIds(taskIds: $taskIds) {
      deleted
    }
  }
`;

/** Hard-reset a single edge task. Drops the BullMQ row, wipes
 *  Firestore claim/extraction metadata, re-installs as fresh
 *  QUEUED. Used to recover tasks BullMQ exhausted but whose
 *  Firestore mirror still says QUEUED. */
export const useResetEdgeTask = (
  client: ApolloClient<NormalizedCacheObject>,
) => {
  type T = Pick<Mutation, 'resetEdgeTask'>;
  return useMutation<T, {taskId: string}>(MUTATION_RESET_EDGE_TASK, {
    client,
    refetchQueries: ['getAdminTasks'],
  });
};

const MUTATION_RESET_EDGE_TASK = gql`
  mutation resetEdgeTask($taskId: ID!) {
    resetEdgeTask(taskId: $taskId) {
      reset
    }
  }
`;

/** Bulk-reset edge tasks by explicit id list. Concurrency-bounded
 *  server-side. Returns the count actually reset. */
export const useResetEdgeTasksByIds = (
  client: ApolloClient<NormalizedCacheObject>,
) => {
  type T = Pick<Mutation, 'resetEdgeTasksByIds'>;
  return useMutation<T, {taskIds: string[]}>(MUTATION_RESET_EDGE_TASKS_BY_IDS, {
    client,
    refetchQueries: ['getAdminTasks'],
  });
};

const MUTATION_RESET_EDGE_TASKS_BY_IDS = gql`
  mutation resetEdgeTasksByIds($taskIds: [ID!]!) {
    resetEdgeTasksByIds(taskIds: $taskIds) {
      reset
    }
  }
`;

/** Bulk-retry every FAILED extraction of a given task type. */
export const useRetryFailedEdgeTaskExtractions = (
  client: ApolloClient<NormalizedCacheObject>,
) => {
  type T = Pick<Mutation, 'retryFailedEdgeTaskExtractions'>;
  return useMutation<T, {taskType: EdgeQueueTaskType; limit?: number}>(
    MUTATION_RETRY_FAILED_EDGE_TASK_EXTRACTIONS,
    {
      client,
      refetchQueries: ['getAdminTasks'],
    },
  );
};

const MUTATION_RETRY_FAILED_EDGE_TASK_EXTRACTIONS = gql`
  mutation retryFailedEdgeTaskExtractions($taskType: EdgeQueueTaskType!, $limit: Int) {
    retryFailedEdgeTaskExtractions(taskType: $taskType, limit: $limit) {
      enqueued
      jobIds
    }
  }
`;

/**
 * Generalized bulk-retry: any subset of types (omit ⇒ all
 * extractable types) over an optional time window
 * (`sinceMs` ⇒ tasks with updatedAt >= sinceMs; omit ⇒ all time).
 * Returns per-type counts so the UI can report what got picked up.
 */
export const useRetryAllFailedEdgeTaskExtractions = (
  client: ApolloClient<NormalizedCacheObject>,
) => {
  type T = Pick<Mutation, 'retryAllFailedEdgeTaskExtractions'>;
  return useMutation<
    T,
    {taskTypes?: EdgeQueueTaskType[] | null; sinceMs?: number | null; limit?: number}
  >(MUTATION_RETRY_ALL_FAILED_EDGE_TASK_EXTRACTIONS, {
    client,
    refetchQueries: ['getAdminTasks'],
  });
};

const MUTATION_RETRY_ALL_FAILED_EDGE_TASK_EXTRACTIONS = gql`
  mutation retryAllFailedEdgeTaskExtractions(
    $taskTypes: [EdgeQueueTaskType!]
    $sinceMs: Float
    $limit: Int
  ) {
    retryAllFailedEdgeTaskExtractions(
      taskTypes: $taskTypes
      sinceMs: $sinceMs
      limit: $limit
    ) {
      enqueued
      scanned
      enqueueFailures
      perTypeCounts {
        taskType
        scanned
        enqueued
        enqueueFailures
      }
    }
  }
`;

/**
 * Batch-fetch BullMQ jobs by id. Used by the Edge tasks tab to
 * render per-row progress for the linked extraction job. Polls
 * every 3 seconds while jobIds is non-empty so the progress bar
 * advances without manual refresh; skips the network entirely
 * when there are no extractionJobIds in the visible task set.
 */
export const useGetBullmqJobsByIds = (
  client: ApolloClient<NormalizedCacheObject>,
  jobIds: string[],
) => {
  type T = Pick<Query, 'getBullmqJobsByIds'>;
  return useQuery<T, {jobIds: string[]}>(QUERY_GET_BULLMQ_JOBS_BY_IDS, {
    client,
    variables: {jobIds},
    skip: jobIds.length === 0,
    fetchPolicy: 'cache-and-network',
    // COST-05: bumped from 3s → 15s on 2026-06-03. Failed-tasks
    // visibility is post-mortem; 15s freshness is fine and the 3s
    // poll was a meaningful share of Firestore reads on every
    // open admin tab.
    pollInterval: 15000,
  });
};

const QUERY_GET_BULLMQ_JOBS_BY_IDS = gql`
  query getBullmqJobsByIds($jobIds: [ID!]!) {
    getBullmqJobsByIds(jobIds: $jobIds) {
      id
      name
      state
      createdAt
      processedAt
      finishedAt
      attempts
      failedReason
      progressJson
      returnValueJson
    }
  }
`;

/**
 * Worker fleet snapshot — connected workers + per-worker active job
 * counts. Polled every 5s so the Workers subpanel reflects fresh
 * state without manual refresh.
 */
export const useGetBullmqWorkers = (
  client: ApolloClient<NormalizedCacheObject>,
) => {
  type T = Pick<Query, 'getBullmqWorkers'>;
  return useQuery<T>(QUERY_GET_BULLMQ_WORKERS, {
    client,
    fetchPolicy: 'cache-and-network',
    // COST-05: bumped from 5s → 15s on 2026-06-03. Worker fleet
    // snapshot doesn't move that fast; 15s is still snappy enough
    // for the Workers subpanel.
    pollInterval: 15000,
  });
};

const QUERY_GET_BULLMQ_WORKERS = gql`
  query getBullmqWorkers {
    getBullmqWorkers {
      totalWorkers
      totalActiveJobs
      concurrencyPerWorker
      concurrencyByQueue {
        queueName
        concurrency
      }
      rateLimitMax
      rateLimitDurationMs
      workers {
        queueName
        name
        addr
        ageSeconds
        idleSeconds
        activeJobs
      }
    }
  }
`;

export const useListBullmqJobs = (
  client: ApolloClient<NormalizedCacheObject>,
  params: {
    states?: BullmqJobState[];
    limit?: number;
    queues?: string[] | null;
    // Job-name allowlist (opt-in). When set, ONLY these names come
    // back. The BG view's Type picker maps to this so an explicit
    // selection gets `limit` rows of that type.
    includeNames?: string[] | null;
    // Job-name denylist applied server-side BEFORE the `limit`
    // truncation. The BG view's default-hidden set rides this so
    // the limit applies to user-visible rows.
    excludeNames?: string[] | null;
  }
) => {
  type T = Pick<Query, 'listBullmqJobs'>;
  return useQuery<T>(QUERY_LIST_BULLMQ_JOBS, {
    client,
    variables: {
      states: params.states && params.states.length > 0 ? params.states : null,
      limit: params.limit ?? 100,
      queues: params.queues && params.queues.length > 0 ? params.queues : null,
      includeNames:
        params.includeNames && params.includeNames.length > 0
          ? params.includeNames
          : null,
      excludeNames:
        params.excludeNames && params.excludeNames.length > 0
          ? params.excludeNames
          : null,
    },
    fetchPolicy: "cache-and-network",
    pollInterval: 5000,
  });
};

const QUERY_LIST_BULLMQ_JOBS = gql`
  query listBullmqJobs(
    $states: [BullmqJobState!]
    $limit: Int
    $queues: [String!]
    $includeNames: [String!]
    $excludeNames: [String!]
  ) {
    listBullmqJobs(
      states: $states
      limit: $limit
      queues: $queues
      includeNames: $includeNames
      excludeNames: $excludeNames
    ) {
      id
      name
      state
      createdAt
      processedAt
      finishedAt
      attempts
      failedReason
      dataJson
      returnValueJson
      progressJson
      logs
    }
  }
`;

export const useCancelBullmqJob = (client: ApolloClient<NormalizedCacheObject>) => {
  type T = Pick<Mutation, 'cancelBullmqJob'>;
  return useMutation<T, { jobId: string }>(MUTATION_CANCEL_BULLMQ_JOB, {
    client,
    refetchQueries: ['listBullmqJobs'],
  });
};

const MUTATION_CANCEL_BULLMQ_JOB = gql`
  mutation cancelBullmqJob($jobId: ID!) {
    cancelBullmqJob(jobId: $jobId)
  }
`;

export const useRemoveBullmqJobs = (client: ApolloClient<NormalizedCacheObject>) => {
  type T = Pick<Mutation, 'removeBullmqJobs'>;
  return useMutation<T, { states: BullmqJobState[] }>(MUTATION_REMOVE_BULLMQ_JOBS, {
    client,
    refetchQueries: ['listBullmqJobs'],
  });
};

const MUTATION_REMOVE_BULLMQ_JOBS = gql`
  mutation removeBullmqJobs($states: [BullmqJobState!]!) {
    removeBullmqJobs(states: $states)
  }
`;

/**
 * Re-run the scoring pipeline (SCORE_FEED / SCORE_HISTORY → selector
 * → SCORE_VIDEO Flow / SCORE_CHANNEL) against an existing edge-task
 * snapshot. Resolved server-side: the resolver looks up the task,
 * hydrates the persisted snapshot, and enqueues the orchestrator.
 */
export const useAdminRerunScoringPipeline = (
  client: ApolloClient<NormalizedCacheObject>,
) => {
  type T = Pick<Mutation, 'adminRerunScoringPipeline'>;
  return useMutation<T, {sourceTaskId: string}>(MUTATION_ADMIN_RERUN_SCORING_PIPELINE, {
    client,
    refetchQueries: ['listBullmqJobs'],
  });
};

const MUTATION_ADMIN_RERUN_SCORING_PIPELINE = gql`
  mutation adminRerunScoringPipeline($sourceTaskId: ID!) {
    adminRerunScoringPipeline(sourceTaskId: $sourceTaskId) {
      source
      sourceTaskId
      videoCount
      jobId
    }
  }
`;

export const useGetTaskDebugInfo = (client: ApolloClient<NormalizedCacheObject>) => {
  type T = Pick<Query, 'getTaskDebugInfo'>;
  const [getTaskDebugInfo, result] = useLazyQuery<T>(QUERY_GET_TASK_DEBUG_INFO, {
    client,
    fetchPolicy: "network-only",
  });

  return { getTaskDebugInfo, ...result };
};

const QUERY_GET_FAILED_TASKS = gql`
  query getFailedTasks($limit: Int, $taskType: EdgeQueueTaskType) {
    getFailedTasks(limit: $limit, taskType: $taskType) {
      id
      profileId
      type
      state
      createdAt
      updatedAt
      startedAt
      completedAt
      retryCount
      maxRetries
      result {
        status
        errorMessage
      }
    }
  }
`;

const QUERY_GET_TASK_DEBUG_INFO = gql`
  query getTaskDebugInfo($taskId: ID!) {
    getTaskDebugInfo(taskId: $taskId) {
      task {
        id
        type
        state
        createdAt
        updatedAt
        startedAt
        completedAt
        retryCount
        maxRetries
        result {
          status
          errorMessage
        }
      }
      rawDataJson
      parseErrors {
        field
        message
        context
      }
    }
  }
`;
